import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BiUserCircle, BiBriefcase, BiTask } from 'react-icons/bi';
import { API_BASE_URL } from '../utils/urls.js';

const UserDetailsPage = () => {
    const { id } = useParams();
    const [userProfile, setUserProfile] = useState(null);
    const [userCases, setUserCases] = useState([]);
    const [userTasks, setUserTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            try {
                // Fetch all necessary data in parallel
                const [profileRes, casesRes, tasksRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/users/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/cases`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/tasks/user/${id}`, { headers: { Authorization: `Bearer ${token}` } })
                ]);

                setUserProfile(profileRes.data);

                // Filter cases to find ones assigned to this user
                const assignedCases = casesRes.data.data.filter(c => c.assigned_to_id === parseInt(id));
                setUserCases(assignedCases);

                setUserTasks(tasksRes.data);

            } catch (err) {
                setError('Failed to fetch user details.');
                console.error('Error fetching details:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserDetails();
    }, [id]);

    if (loading) return <div className="text-center text-gray-500 py-10">Loading user details...</div>;
    if (error) return <div className="text-center text-red-500 py-10">{error}</div>;
    if (!userProfile) return <div className="text-center text-gray-500 py-10">No user data found.</div>;

    return (
        <div className="container mx-auto p-4 sm:p-6 max-w-4xl">
            {/* User Profile Card */}
            <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
                <div className="flex items-center space-x-4 mb-6">
                    <BiUserCircle className="w-16 h-16 text-blue-500" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">{userProfile.first_name} {userProfile.last_name}</h1>
                        <p className="text-gray-600 capitalize">{userProfile.role_name.replace('_', ' ')}</p>
                    </div>
                </div>

                <dl className="divide-y divide-gray-200">
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.email}</dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.phone_number || 'N/A'}</dd>
                    </div>
                </dl>
            </div>

            {/* Assigned Cases Table */}
            <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center"><BiBriefcase className="mr-2" /> Assigned Cases ({userCases.length})</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Case Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {userCases.length > 0 ? userCases.map(c => (
                                <tr key={c.id}>
                                    <td className="px-4 py-2 text-sm text-blue-600 hover:underline"><Link to={`/cases/${c.id}`}>{c.case_name}</Link></td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{c.status}</td>
                                </tr>
                            )) : <tr><td colSpan="2" className="text-center py-4 text-sm text-gray-500">No cases assigned to this user.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Assigned Tasks Table */}
            <div className="bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center"><BiTask className="mr-2" /> Assigned Tasks ({userTasks.length})</h2>
                <div className="overflow-x-auto">
                     <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task Title</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {userTasks.length > 0 ? userTasks.map(t => (
                                <tr key={t.id}>
                                    <td className="px-4 py-2 text-sm text-blue-600 hover:underline"><Link to={`/tasks/edit/${t.id}`}>{t.task_title}</Link></td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{t.status}</td>
                                </tr>
                            )) : <tr><td colSpan="2" className="text-center py-4 text-sm text-gray-500">No tasks assigned to this user.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserDetailsPage;